"use client";

import { Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { apiUrl } from "@/lib/aicheck/api-base";
import { authService } from "@/services/auth";

interface DoneMeta {
  gist: string | null;
  servedBy: "local" | "cloud" | "none";
  truncated: boolean;
}

type State =
  | { status: "loading" }
  | { status: "streaming"; text: string }
  | { status: "done"; data: DoneMeta };

/**
 * 指摘エグゼクティブ要約（A′）の表示（SSE ストリーミング対応）。
 * - データ（指摘リスト）表示は遅らせない。生成は描画後（アイドル時）に開始する。
 * - 生成中は「AI要約を生成中…」→ 部分テキストが届き始めたら逐次表示（待ち感の軽減）。
 *   接続先が非対応なら全文が 1 チャンクで届くため一括表示になる（同じコード）。
 * - ローカル LLM 不在 / gist 無しなら何も描画しない（グレースフル劣化）。
 * - 「AI要約(参考)」ラベル固定。権威は下の指摘リスト側。使い捨て。
 */
export function FindingsAiSummary({ runId }: { runId: string | undefined }) {
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    setState({ status: "loading" });

    const finishHidden = () => {
      if (!cancelled) {
        setState({
          status: "done",
          data: { gist: null, servedBy: "none", truncated: false },
        });
      }
    };

    // SSE を解析して delta を逐次反映、done で確定する。
    const consume = async (body: ReadableStream<Uint8Array>) => {
      const reader = body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (cancelled) {
          await reader.cancel();
          return;
        }
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";
        for (const evt of events) {
          let event = "message";
          let data = "";
          for (const line of evt.split("\n")) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).trim();
          }
          if (!data) continue;
          if (event === "delta") {
            try {
              const { t } = JSON.parse(data) as { t: string };
              acc += t;
              if (!cancelled) setState({ status: "streaming", text: acc });
            } catch {
              /* 分割チャンク: 次で補完 */
            }
          } else if (event === "done") {
            try {
              const meta = JSON.parse(data) as DoneMeta;
              // 逐次で受けた本文を優先。空なら done.gist を採用。
              const gist = acc.length > 0 ? acc : meta.gist;
              if (!cancelled) {
                setState({ status: "done", data: { ...meta, gist } });
              }
            } catch {
              finishHidden();
            }
          }
        }
      }
    };

    const start = () => {
      void (async () => {
        try {
          const token = await authService.getIdToken();
          if (cancelled) return;
          if (!token) {
            finishHidden();
            return;
          }
          const res = await fetch(
            apiUrl(
              `/api/aicheck/run/result/${encodeURIComponent(runId)}/summary/stream`,
            ),
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (cancelled) return;
          if (!res.ok || !res.body) {
            finishHidden();
            return;
          }
          await consume(res.body);
        } catch {
          // 付加機能。失敗は静かに無表示。
          finishHidden();
        }
      })();
    };

    const w = window as unknown as {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(start, { timeout: 2000 });
    } else {
      timerId = setTimeout(start, 500);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined && typeof w.cancelIdleCallback === "function") {
        w.cancelIdleCallback(idleId);
      }
      if (timerId !== undefined) clearTimeout(timerId);
    };
  }, [runId]);

  if (!runId) return null;

  // 生成中インジケータ（まだ本文が届いていない）
  if (state.status === "loading") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50/50 px-4 py-2.5 text-xs text-indigo-400">
        <Sparkles size={14} className="animate-pulse" />
        AI要約を生成中…
      </div>
    );
  }

  // 逐次表示中（部分テキスト + カーソル）
  if (state.status === "streaming") {
    return (
      <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 px-4 py-3">
        <div className="mb-1 flex items-center gap-1.5">
          <Sparkles size={14} className="animate-pulse text-indigo-500" />
          <span className="text-[11px] font-semibold text-indigo-600">
            AI要約（参考）
          </span>
        </div>
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
          {state.text}
          <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-indigo-300 align-middle" />
        </p>
      </div>
    );
  }

  // gist が出なければ何も出さない（ローカル LLM 不在時の劣化）
  if (!state.data.gist) return null;

  return (
    <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 px-4 py-3">
      <div className="mb-1 flex items-center gap-1.5">
        <Sparkles size={14} className="text-indigo-500" />
        <span className="text-[11px] font-semibold text-indigo-600">
          AI要約（参考）
        </span>
        {state.data.truncated && (
          <span className="text-[10px] text-indigo-400">
            （指摘が多いため一部を要約）
          </span>
        )}
      </div>
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
        {state.data.gist}
      </p>
    </div>
  );
}
